// SB6-ANA-FILT-06 (D11) — render the Markdown info copy from `infoCopy.ts`
// inside the per-level tooltips and the general info popover.
//
// `react-markdown` is sanitised by default: it does NOT pass raw HTML
// through (no `rehype-raw` plugin is installed), so embedded `<script>` /
// `<img onerror>` and friends are rendered as inert text, not executed.
// We therefore do not need a separate DOMPurify pass for the trusted,
// in-repo copy strings.
//
// MUI typography is applied via `components` overrides so the rendered
// headings / lists / emphasis match the surrounding chrome at the small
// sizes used in tooltips and the popover.
import { Box, Link, Typography } from "@mui/material";
import Markdown from "react-markdown";

interface MarkdownInfoProps {
  /** Markdown source (trusted, from `infoCopy.ts`). */
  children: string;
  /**
   * Tooltips sit on a dark surface where `text.primary` is too low
   * contrast; pass `"inherit"` so the copy takes the tooltip's own
   * (light) colour. The popover uses the default theme colours.
   */
  variant?: "tooltip" | "popover";
}

export function MarkdownInfo({
  children,
  variant = "popover",
}: MarkdownInfoProps): React.ReactElement {
  const onTooltip = variant === "tooltip";
  const bodyColor = onTooltip ? "inherit" : "text.secondary";
  const headingColor = onTooltip ? "inherit" : "text.primary";
  return (
    <Box
      data-testid="markdown-info"
      sx={{
        // Tight rhythm so the copy reads as a compact block, not a page.
        "& > :first-of-type": { mt: 0 },
        "& > :last-child": { mb: 0 },
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <Markdown
        components={{
          h1: ({ children: c }) => (
            <Typography
              variant="subtitle2"
              component="h2"
              sx={{ fontWeight: 700, mb: 0.5, color: headingColor }}
            >
              {c}
            </Typography>
          ),
          h2: ({ children: c }) => (
            <Typography
              variant="subtitle2"
              component="h2"
              sx={{ fontWeight: 700, mb: 0.5, color: headingColor }}
            >
              {c}
            </Typography>
          ),
          h3: ({ children: c }) => (
            <Typography
              variant="subtitle2"
              component="h3"
              sx={{ fontWeight: 700, mb: 0.5, color: headingColor }}
            >
              {c}
            </Typography>
          ),
          p: ({ children: c }) => (
            <Typography
              variant="body2"
              component="p"
              sx={{ my: 0.5, color: bodyColor }}
            >
              {c}
            </Typography>
          ),
          ul: ({ children: c }) => (
            <Box
              component="ul"
              sx={{ my: 0.5, pl: 2.5, color: bodyColor, fontSize: 13 }}
            >
              {c}
            </Box>
          ),
          li: ({ children: c }) => (
            <Box component="li" sx={{ mb: 0.25 }}>
              {c}
            </Box>
          ),
          strong: ({ children: c }) => (
            <Box
              component="strong"
              sx={{ fontWeight: 700, color: headingColor }}
            >
              {c}
            </Box>
          ),
          em: ({ children: c }) => <Box component="em">{c}</Box>,
          a: ({ children: c, href }) => (
            <Link href={href} target="_blank" rel="noopener noreferrer">
              {c}
            </Link>
          ),
        }}
      >
        {children}
      </Markdown>
    </Box>
  );
}
